import { NewWindowIcon } from '@pm/icons';

import { clsx } from 'clsx';
import { Element } from 'hast';
import Linkify from 'linkify-react';
import { JSX, ReactNode } from 'react';
import invariant from 'tiny-invariant';
import { TextLink } from '../../components/TextLink/TextLink';

const variantStyles: Record<string, string> = {
  default: 'text-content-default',
  subdued: 'text-content-subdued',
};

type returnType = {
  [key: string]: (
    props: { children?: ReactNode } & {
      node?: Element;
      href?: string;
    },
  ) => JSX.Element;
};

type Props = {
  variant: keyof typeof variantStyles;
};

// eslint-disable-next-line react-x/no-unnecessary-use-prefix -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
export const useMarkdownComponents = ({
  variant = 'default',
}: Props): returnType => {
  return {
    p: ({ node, ...props }) =>
      typeof props.children === 'string' ? (
        <Linkify
          options={{
            target: '_blank',
            className:
              'text-content-link underline hover:text-content-link-hover',
          }}
        >
          <div
            className={clsx(variantStyles[variant], 'whitespace-pre-wrap')}
            {...props}
          />
        </Linkify>
      ) : (
        <div
          className={clsx(variantStyles[variant], 'whitespace-pre-wrap')}
          {...props}
        />
      ),
    ul: ({ node, ...props }) => (
      <ul
        {...props}
        className={clsx(variantStyles[variant], 'list-disc pl-6 text-wrap')}
      />
    ),
    ol: ({ node, ...props }) => (
      <ol
        {...props}
        className={clsx(variantStyles[variant], 'list-decimal pl-6 text-wrap')}
      />
    ),
    a: ({ node, href, ...props }) => {
      invariant(
        href,
        'Href is required for anchor tags in questionnaire markdown descriptions',
      );
      // @note - https://github.com/purposemed/core/issues/8629 To be reverted after resources work
      return (
        <TextLink target="_blank" to={href} external>
          <div className="break-all inline-block">
            <NewWindowIcon className="h-5 w-5 inline-block mr-1" />
            {props.children}
          </div>
        </TextLink>
      );
    },
    em: ({ node, ...props }) => (
      <small {...props} style={{ fontSize: '0.75rem', lineHeight: '0rem' }} />
    ),
  };
};
